import type { AiCampaignForm, GeneratedTemplate } from "./AiCampaignTypes";

export interface WizardSnapshot {
  stepIndex: number;
  form: AiCampaignForm;
  sendTo: string[];
  excludeList: string[];
  replyTo: string;
  template: GeneratedTemplate | null;
  signature: string | null;
}

const AI_WIZARD_PREFIX = "ai-campaign-wizard:";

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === "string");

const isSnapshot = (value: unknown): value is WizardSnapshot =>
  isRecord(value) &&
  typeof value.stepIndex === "number" &&
  isRecord(value.form) &&
  isStringArray(value.sendTo) &&
  isStringArray(value.excludeList) &&
  typeof value.replyTo === "string";

export const readSessionSnapshot = <T>(
  storageKey: string,
  isValid: (_value: unknown) => _value is T
): T | null => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeSessionSnapshot = (storageKey: string, snapshot: unknown) => {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch {
    // storage full or unavailable: the wizard keeps working without refresh recovery
  }
};

export const clearSessionSnapshot = (storageKey: string) => {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // nothing to clear
  }
};

export const readWizardSnapshot = (key: string): WizardSnapshot | null =>
  readSessionSnapshot(AI_WIZARD_PREFIX + key, isSnapshot);

export const writeWizardSnapshot = (key: string, snapshot: WizardSnapshot) =>
  writeSessionSnapshot(AI_WIZARD_PREFIX + key, snapshot);

export const clearWizardSnapshot = (key: string) => clearSessionSnapshot(AI_WIZARD_PREFIX + key);
